'use client';

import { cn } from '@insta-club/utilities';
import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';

const wrapperVariants = cva('flex items-start gap-3 cursor-pointer', {
    variants: {
        disabled: {
            true: 'opacity-50 cursor-not-allowed',
            false: ''
        }
    },
    defaultVariants: { disabled: false }
});

const customRadioVariants = cva(
    [
        'relative mt-1 size-5 rounded-full border border-gray-300',
        'cursor-pointer',
        "after:content-[''] after:absolute after:inset-1 after:rounded-full after:scale-0 after:transition-transform",
        'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2',
        'peer-checked:after:scale-100',
        'peer-disabled:opacity-50 peer-disabled:cursor-not-allowed',
        'transition'
    ].join(' '),
    {
        variants: {
            color: {
                red: 'after:bg-primary-600 peer-focus-visible:outline-primary-600 peer-checked:border-primary-600',
                blue: 'after:bg-blue-600    peer-focus-visible:outline-blue-600    peer-checked:border-blue-600',
                green: 'after:bg-green-600   peer-focus-visible:outline-green-600   peer-checked:border-green-600',
                light: 'after:bg-primary-500   peer-focus-visible:outline-white   peer-checked:border-white'
            }
        },
        defaultVariants: { color: 'red' }
    }
);

interface RadioButtonProps
    extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'>,
        VariantProps<typeof customRadioVariants> {
    name: string;
    value: string | number;
    label: React.ReactNode;
    description?: React.ReactNode;
    checked?: boolean;
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
    disabled?: boolean;
    className?: string;
    inputClassName?: string;
    controlClassName?: string;
    id?: string;
    color?: 'red' | 'blue' | 'green' | 'light';
}

export default function RadioButton({
    name,
    value,
    label,
    description,
    checked,
    onChange,
    disabled = false,
    color = 'red',
    className,
    inputClassName,
    controlClassName,
    id,
    ...rest
}: RadioButtonProps) {
    const inputId = id ?? `${name}-${String(value).replace(/\s+/g, '-')}`;

    return (
        <label className={cn(wrapperVariants({ disabled }), className)} dir='rtl' htmlFor={inputId}>
            <input
                id={inputId}
                type='radio'
                name={name}
                value={value}
                checked={checked}
                onChange={onChange}
                disabled={disabled}
                className={cn('peer sr-only', inputClassName)}
                {...rest}
            />

            <span aria-hidden='true' className={cn(customRadioVariants({ color }), controlClassName)} />

            <div className='flex-1 text-right'>
                <div className='text-sm font-semibold text-gray-600'>{label}</div>
                {description && <div className='mt-1 text-[13px] text-gray-500'>{description}</div>}
            </div>
        </label>
    );
}

